import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  breakpoints,
  Heading,
  PageBlock,
  Stack,
  Text,
  textColors,
  textVariants,
  vars,
  Card,
} from '@etg/wings';
import { Collection, DevBar } from '@eti/debug';
import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Profiler } from '@eti/utils';
import { useMutation } from '@apollo/client';
import { InView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';
import { OFFERED_PRODUCTS } from '../../../traveler-details/constants/formFields';
import { useKindly } from '../../../../common/custom-hooks/useKindly';
import {
  CREATING_ORDER_FLAG,
  POST_BOOKING_ORDER_FLAG,
} from '../../../../constants/receiptConstants';
import { isDev } from '../../../../common/debug/utils';
import {
  DebugButton,
  DebugButtonPaymentPageMockConfig,
  DebugCommonComponents,
  DebugGraphQlResponses,
} from '../../../../common/debug/components';
import { PAYMENT } from '../../../../constants/bookingFlowSteps';
import { useScrollTop } from '../../../../common/custom-hooks/scrolling';
import { getDestination, getOriginAndDestinationCodesFromBound } from '../../../../utils/format';
import { transformPaymentMethod } from '../../../../utils/paymentMethodsTS';
import {
  getCheapestPaymentMethod,
  getSummaryForPaymentMethod,
  usePaymentMethodTexts,
} from '../../../../utils/paymentMethods';
import StepIndicator from '../../../../common/step-indicator/BookingFlowStepIndicator';
import { shouldShowDebugProviderSystemBar } from '../../../../utils/debug';
import DebugProviderSystemBar from '../../../../widgets/trip-details/components/DebugProviderSystemBar';
import { routes } from '../../../../constants/routesAndApisConstants';
import NoSelectedTripErrorDialog from '../../../../common/error-modals/NoSelectedTripErrorDialog';
import { useEcommerceDataLayerEffect } from '../../../../common/data-layer/ecommerce/custom-hooks/useEcommerceDataLayerEffect';
import { useBeginCheckoutDataLayerEffect } from '../../../../common/data-layer/ecommerce/custom-hooks/useBeginCheckoutDataLayerEffect';
import { useBrandedFareName } from '../../../../common/custom-hooks/useBrandedFareName';
import useInternalPayment from '../../hooks/useInternalPayment';
import useStopLoss from '../../hooks/useStopLoss';
import PaymentDialog from '../../components/payment-dialog/PaymentDialog';
import PaymentNotifications from '../../components/PaymentNotifications';
import UrlParamErrorDialog from '../../components/UrlParamErrorDialog';
import { TAX_ID } from '../../constants/formFields';
import { dialogStates } from '../../constants/paymentDialog';
import { paymentMethodCodes, paymentMethodTypes } from '../../constants/paymentMethods';
import {
  getBinNumber,
  shouldRefetchWithBinNumber,
} from '../../payment-methods/card/utils/binVerification';
import PaymentMethods from '../../payment-methods/components/PaymentMethods';
import PaymentCart from '../../containers/PaymentPageCartContainer';
import { STORE_STATISTICS_MUTATION } from '../../graphql/mutations';
import { getSelectedPaymentMethod } from '../utils/paymentMethod';
import BrandedFare from '../../components/branded-fare/BrandedFare';
import { getTicketingCarrierCodes } from '../../../traveler-details/utils/carriers';
import PaymentDisabledMessage from '../../components/PaymentDisabledMessage';
import { makeOrderResponseMessages } from '../../constants/makeOrderResponse';
import useCadillac from '../../hooks/useCadillac';
import Layout from '../../components/Layout';
import PaymentBottomContent from '../../components/PaymentBottomContent';
import { getMethod, useEligiblePaymentMethods } from '../../utils/paymentMethods';
import { scrollToElement } from '../../../../utils/scrollToElement';
import AdditionalTax from '../../components/AdditionalTax';
import CFCInformationCard from '../../components/CFCInformationCard';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../../utils/carriersUtils';
import PriceChangeNotification from '../../components/PriceChangeNotification';
import { getTripNotificationChanges } from '../../../../utils/notification';
import Discount from '../../components/discount/components/Discount';
import BusinessReceipt from '../../common/business-receipt/BusinessReceipt';
import { useGetBlue, eventTypes } from '../../../../utils/useGetBlue';
import { useTaxFormSectionForPaymentPage } from '../../hooks/useTaxFormSection';
import TaxFormSection from '../../components/tax-form/TaxFormSection';
import { propTypes as paymentPropTypes } from './propTypes';
import PaymentLinkCart from './PaymentLinkCart';

const wrapperStyles = css`
  background-color: ${vars.colors.page.background.primary};
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const legalTextStyles = css`
  max-width: 80ch;
`;

const mobileTripDetailsPortalContainerStyles = css`
  @media (min-width: ${breakpoints._970}) {
    display: none;
  }
`;

const PaymentPage = ({
  bounds = [],
  bundles,
  cadillac,
  cardNumber,
  cartId,
  change,
  clearFields,
  cabinBaggageTripSummary,
  chargeCurrencyPriceFormat,
  checkedBaggageSummary,
  customerContactInfo,
  customerEmail,
  customerPhone,
  discountAmount,
  fareRules,
  fillForm,
  form,
  hasPotentialForAdditionalTax = false,
  handleSubmit,
  headerAndInfoHeight = 0,
  initialize,
  isMultiStop,
  isOtherChargeCurrency,
  isPanAndDoBDisabled,
  isPriceChange,
  isPaymentLinkCart = false,
  onDeclineTripChange,
  paymentLinkCartId,
  paymentLinkCartPrice,
  paymentMethodPrices,
  paymentMethods,
  personalItemTripSummary,
  preloadReceiptPage,
  populatePriceChangeDisplayed,
  products,
  serviceFee,
  refetchPaymentQuery,
  reset,
  selectedCommercialCardId,
  selectedMainPaymentMethod,
  selectedPaymentMethodCode,
  selectedPaymentMethodId,
  selectedPaymentMethodType,
  selectedSubsectionCode,
  selectionKey,
  setHasPendingPayment,
  subtotalPrice,
  transactionId,
  travelerPrices,
  travelers,
  tripChanges,
  tripCharacteristics = [],
  tripProviderSystems,
  populateTaxId,
  includedExtraProducts,
  includedCabinBaggage,
}) => {
  const { t } = useTranslation();
  const { isLoading: isLoadingPaymentMethods, eligiblePaymentMethods } =
    useEligiblePaymentMethods(paymentMethods);
  useInternalPayment(paymentMethods, selectedPaymentMethodCode, change, clearFields);
  useEffect(() => {
    const persistedTaxId = sessionStorage.getItem(TAX_ID);
    if (persistedTaxId) {
      populateTaxId(persistedTaxId);
    }
  }, [populateTaxId]);

  useEcommerceDataLayerEffect(bounds, bundles, discountAmount, cartId, products, travelerPrices);
  useBeginCheckoutDataLayerEffect(bundles, products, travelerPrices, isPaymentLinkCart);
  const { p } = useProperty();
  const { pathname } = useLocation();
  const sidebarRef = useRef();
  const onDeclineTripChangeRef = useRef(() => {});
  const [methodBinNumber, setMethodBinNumber] = useState(null);
  const [errorMessageId, setErrorMessageId] = useState(null);
  const [isPriceChangeDeclined, setIsPriceChangeDeclined] = useState(false);
  const [refetchCart, setRefetchCart] = useState(() => ({
    current: null,
  }));
  const [dialogState, setDialogState] = useState(null);
  const [priceChanges, setPriceChanges] = useState(null);
  const [shouldShowLoadingDots, setShouldShowLoadingDots] = useState(true);
  const [submittedWithNoPaymentMethod, setSubmittedWithNoPaymentMethod] = useState(false);
  const [isCFCModalOpen, setIsCFCModalOpen] = useState(false);

  useScrollTop(dialogState === dialogStates.TRANSITION);
  const paymentMethodTexts = usePaymentMethodTexts();
  const { isPotentialStopLossChange, setIsStopLossChange } = useStopLoss();
  const { shouldShowTaxFormSection } = useTaxFormSectionForPaymentPage(isPanAndDoBDisabled);

  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const isPriceChangeEnabled = p('Payment.PriceChangeDialogSummary.Enabled');
  const { price: priceChange } = getTripNotificationChanges(tripChanges, bounds);
  const shouldShowDiscount = p('Payment.DiscountCode.Enabled') && !isPaymentLinkCart;
  const businessReceiptEnabled = p('Payment.BusinessReceipt.Enabled');

  useKindly();
  useCadillac(cadillac, transactionId);

  useGetBlue(eventTypes.BASKET, getOriginAndDestinationCodesFromBound(bounds[0]));

  useEffect(() => {
    if (!selectedPaymentMethodType) {
      return;
    }

    if (selectedPaymentMethodType === paymentMethodTypes.CARD) {
      const binNumber = getBinNumber(cardNumber);
      if (shouldRefetchWithBinNumber(binNumber, methodBinNumber)) {
        setMethodBinNumber(binNumber);
      }
    }
  }, [selectedPaymentMethodType, cardNumber, methodBinNumber]);

  const { isSnowplowTrackingEnabled, updateSnowplowContextData, sendTrackEvent } =
    useSnowplowTracking();

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      const selectedProducts = products.map(({ title }) => title).join(', ');

      updateSnowplowContextData({
        productData: {
          selectedProducts,
        },
      });

      sendTrackEvent({ category: 'Payment', action: 'Seen', label: 'Page' });
    }
  }, [isSnowplowTrackingEnabled, products, sendTrackEvent, updateSnowplowContextData]);

  const onPaymentResponseProblem = useCallback(
    (messageId) => {
      if (messageId) {
        setErrorMessageId(messageId);
        setDialogState(dialogStates.ERROR);
      } else {
        setDialogState(null);
      }

      setHasPendingPayment(false);
    },
    [setHasPendingPayment],
  );

  const handleMakeOrderMutationError = () => {
    onPaymentResponseProblem('GENERIC_ERROR');
  };

  const selectedPaymentMethod = getSelectedPaymentMethod(
    selectedPaymentMethodCode,
    selectedPaymentMethodType,
    selectedPaymentMethodId,
    selectedCommercialCardId,
    eligiblePaymentMethods,
    selectedSubsectionCode,
  );

  /*
   *  TODO WEBTECH-943: `currentPaymentMethod` is used in the cart and in the summary, to
   *                    show the price/label of either the selected, or cheapest, method.
   *                    Look into refactoring this, once we have a cleaner way to resolve
   *                    the selected payment method (see line #238 above).
   */

  const currentPaymentMethod = getSummaryForPaymentMethod(
    selectedPaymentMethod
      ? transformPaymentMethod(selectedPaymentMethod)
      : getCheapestPaymentMethod(paymentMethodPrices),
    paymentMethodTexts,
  );

  const triggerPayment = useCallback(
    (methodCallback) => {
      sessionStorage.setItem(CREATING_ORDER_FLAG, '1');
      sessionStorage.setItem(
        'cartData',
        JSON.stringify({
          bundles,
          coupon: sessionStorage.getItem('discountCode'),
          discountAmount: discountAmount ?? 0,
          products,
          selectedPaymentMethod,
          totalPrice: priceChanges?.priceNew ?? subtotalPrice,
          transactionId,
          travelerPrices,
        }),
      );
      if (isPaymentLinkCart) {
        sessionStorage.setItem(POST_BOOKING_ORDER_FLAG, 'true');
        sessionStorage.setItem('postbookingProducts', JSON.stringify(products));
      }
      setDialogState(dialogStates.TRANSITION);
      setHasPendingPayment(true);
      methodCallback();
    },
    [
      bundles,
      discountAmount,
      isPaymentLinkCart,
      priceChanges,
      products,
      selectedPaymentMethod,
      setHasPendingPayment,
      subtotalPrice,
      transactionId,
      travelerPrices,
    ],
  );

  const isNewStoreStatisticSol = p('Offered.Product.Cache.Enabled');

  const sessionItem = isNewStoreStatisticSol ? OFFERED_PRODUCTS : selectionKey;
  const [storeStatistics] = useMutation(STORE_STATISTICS_MUTATION, {
    onCompleted: () => {
      sessionStorage.removeItem(sessionItem);
    },
  });

  useEffect(() => {
    const isPaymentPage = pathname === '/payment';
    if (isNewStoreStatisticSol) {
      if (dialogState === dialogStates.TRANSITION) {
        storeStatistics({
          variables: {
            offeredProducts: [],
            brandedFareOffered: sessionStorage.getItem('brandedFareOffered') === 'true',
            paymentRequest: isPaymentPage,
          },
        });
      }
    } else {
      const offeredProducts = JSON.parse(sessionStorage.getItem(selectionKey)) || [];
      if (dialogState === dialogStates.TRANSITION && offeredProducts.length > 0) {
        storeStatistics({
          variables: {
            offeredProducts,
            brandedFareOffered: sessionStorage.getItem('brandedFareOffered') === 'true',
            paymentRequest: isPaymentPage,
          },
        });
      }
    }
  }, [dialogState, storeStatistics, pathname, selectionKey, isNewStoreStatisticSol]);

  const handleOnErrorDismiss = () => {
    setDialogState(null);
  };

  const handleMakeOrderMutationCompleted = () => {
    setDialogState(null);
  };

  const handlePriceChange = (changes) => {
    setPriceChanges(changes);
    setIsStopLossChange();
    setDialogState(dialogStates.PRICE_CHANGE);
  };

  const handleAcceptPriceChange = (onSubmitCallback) => {
    setPriceChanges(null);
    setDialogState(dialogStates.TRANSITION);
    onSubmitCallback();
  };

  const handleDeclinePriceChange = async () => {
    setPriceChanges(null);
    setDialogState(null);
    onDeclineTripChangeRef.current();
    await onDeclineTripChange();
    setIsPriceChangeDeclined(true);
    setHasPendingPayment(false);
  };

  const pageTitle = t('Payment.Title.Text');

  const { brandedFareInformation, marketingCarrier } =
    bounds?.[0]?.segments?.filter((segment) => {
      return segment.__typename === 'TripSegment';
    })[0] || {};

  const brandedFareName = useBrandedFareName({
    brandedFareInformation,
    marketingCarrier,
  });

  if (JSON.parse(sessionStorage.getItem('hasPaymentFailed'))) {
    return <NoSelectedTripErrorDialog />;
  }

  const cardPaymentMethod = getMethod(eligiblePaymentMethods ?? [], paymentMethodCodes.CARD);

  const getPotentialChargeInDifferentCurrencyText = () => {
    if (currentPaymentMethod.type !== paymentMethodTypes.CARD || !cardPaymentMethod) {
      return null;
    }

    if (!cardPaymentMethod.potentialChargeInDifferentCurrency) {
      return null;
    }

    if (!cardPaymentMethod.potentialChargeInCurrencies) {
      return null;
    }

    return t(
      'Payment.ChargeInDifferentCurrencyNote.Text',
      cardPaymentMethod.potentialChargeInCurrencies.join(', '),
    );
  };

  const isPayInternalPayment =
    eligiblePaymentMethods?.length === 1 &&
    eligiblePaymentMethods?.some((method) => method.code === paymentMethodTypes.PAY_INTERNAL);

  const paymentButtonText = isPayInternalPayment
    ? t('Payment.ZeroAmountCart.PayButton.Label')
    : t('Button.Text.Pay');

  const handleSubmitWithNoPaymentMethod = () => {
    setSubmittedWithNoPaymentMethod(true);
    scrollToElement('#choosePaymentMethodHeader', 80);
    if (isSnowplowTrackingEnabled && pathname === routes.PAYMENT) {
      sendTrackEvent({
        category: 'Payment',
        action: 'Error',
        label: 'Validation Error',
      });
    }
  };

  const header = (
    <>
      {pageTitle && <Heading level={1}>{pageTitle}</Heading>}
      {brandedFareName && (
        <BrandedFare
          brandedFareInformation={brandedFareInformation}
          brandedFareName={brandedFareName}
          cabinBaggageTripSummary={cabinBaggageTripSummary}
          checkedBaggageSummary={checkedBaggageSummary}
          isPersonalItemIncluded={includedCabinBaggage?.includedPersonalItem}
          marketingCarrier={marketingCarrier}
          ticketingCarrierCodes={getTicketingCarrierCodes(bounds)}
        />
      )}
      <div className={mobileTripDetailsPortalContainerStyles} id="mobileTripDetailsButton" />
    </>
  );

  return (
    <div className={wrapperStyles}>
      {pathname !== routes.PAYMENT_LINK && !p('TravelerDetails.DynamicFlow.Enabled') && (
        <StepIndicator currentStep={PAYMENT} />
      )}
      {shouldShowDebugProviderSystemBar(tripProviderSystems) && (
        <DebugProviderSystemBar providerSystems={tripProviderSystems} />
      )}
      <PageBlock withPaddingY>
        <div data-testid="debug-cart-placeholder" />
        <Stack spacing={16}>
          <PaymentNotifications
            bounds={bounds}
            isPotentialStopLossChange={isPotentialStopLossChange && !isPaymentLinkCart}
            tripChanges={tripChanges}
            tripCharacteristics={tripCharacteristics}
          />
          <InView
            as="div"
            data-testid="paymentPage-paymentMethodsAndCart-intersectionWrapper"
            onChange={preloadReceiptPage}
            triggerOnce
          >
            <div data-testid="paymentPage-paymentMethodsAndCart-container">
              <Profiler id="PaymentPage-PaymentMethods">
                <div>
                  <Stack spacing={16}>
                    {p('PaymentPage.Payment.Disabled') ? (
                      <>
                        {header}
                        <PaymentDisabledMessage />
                      </>
                    ) : (
                      <Layout
                        bottomContent={
                          <>
                            <PaymentBottomContent
                              bounds={bounds}
                              chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
                              discountAmount={discountAmount}
                              fareRules={fareRules}
                              form={form}
                              handleSubmit={handleSubmit}
                              isCFCModalOpen={isCFCModalOpen}
                              isOtherChargeCurrency={isOtherChargeCurrency}
                              isPaying={dialogState === dialogStates.TRANSITION}
                              isPaymentLinkCart={isPaymentLinkCart}
                              isPriceChangeDeclined={isPriceChangeDeclined}
                              onSubmitWithNoPaymentMethod={handleSubmitWithNoPaymentMethod}
                              paymentButtonText={paymentButtonText}
                              paymentLinkCartPrice={paymentLinkCartPrice}
                              paymentMethodPrices={paymentMethodPrices}
                              potentialChargeInDifferentCurrencyText={getPotentialChargeInDifferentCurrencyText()}
                              refetchCart={refetchCart}
                              refetchPaymentQuery={refetchPaymentQuery}
                              selectedPaymentMethod={currentPaymentMethod}
                              setIsCFCModalOpen={setIsCFCModalOpen}
                              shouldShowLoadingDots={shouldShowLoadingDots}
                              subtotalPrice={subtotalPrice}
                            />
                            <Text
                              as="p"
                              asHtml
                              className={legalTextStyles}
                              color={textColors.BLACK}
                              variant={textVariants._12}
                            >
                              {t('PaymentPage.LegalMessage.Text')}
                            </Text>
                          </>
                        }
                        header={header}
                        sidebar={
                          <>
                            {!isPaymentLinkCart && (
                              <PaymentCart
                                binNumber={methodBinNumber}
                                bounds={bounds}
                                bundles={bundles}
                                cabinBaggageTripSummary={cabinBaggageTripSummary}
                                chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
                                checkedBaggageSummary={checkedBaggageSummary}
                                customerContactInfo={customerContactInfo}
                                customerEmail={customerEmail}
                                customerPhone={customerPhone}
                                discountAmount={discountAmount}
                                headerAndInfoHeight={headerAndInfoHeight}
                                includedExtraProducts={includedExtraProducts}
                                isMultiStop={isMultiStop}
                                isOtherChargeCurrency={isOtherChargeCurrency}
                                paymentMethodPrices={paymentMethodPrices}
                                personalItemTripSummary={personalItemTripSummary}
                                products={products}
                                selectedCommercialCardId={selectedCommercialCardId}
                                selectedPaymentMethod={currentPaymentMethod}
                                selectedPaymentMethodId={selectedPaymentMethodId}
                                serviceFee={serviceFee}
                                setRefetch={setRefetchCart}
                                sidebarRef={sidebarRef}
                                subTotalPrice={subtotalPrice}
                                travelers={travelers}
                                tripCharacteristics={tripCharacteristics}
                                tripProviderSystems={tripProviderSystems}
                              />
                            )}
                            {isPaymentLinkCart && (
                              <PaymentLinkCart
                                cartPrice={paymentLinkCartPrice}
                                chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
                                discountAmount={discountAmount}
                                headerAndInfoHeight={headerAndInfoHeight}
                                isOtherChargeCurrency={isOtherChargeCurrency}
                                paymentMethodPrices={paymentMethodPrices}
                                products={products}
                                selectedPaymentMethod={currentPaymentMethod}
                              />
                            )}
                          </>
                        }
                        sidebarRef={sidebarRef}
                      >
                        <PaymentMethods
                          bounds={bounds}
                          change={change}
                          discountAmount={discountAmount}
                          fareRules={fareRules}
                          form={form}
                          handleSubmit={handleSubmit}
                          initialize={initialize}
                          isLoading={isLoadingPaymentMethods}
                          isPanAndDoBDisabled={isPanAndDoBDisabled}
                          isPayInternalPayment={isPayInternalPayment}
                          isPaymentLinkCart={isPaymentLinkCart}
                          isPriceChange={isPriceChange}
                          isPriceChangeDeclined={isPriceChangeDeclined}
                          onDeclineTripChangeRef={onDeclineTripChangeRef}
                          onMakeOrderMutationCompleted={handleMakeOrderMutationCompleted}
                          onMakeOrderMutationError={handleMakeOrderMutationError}
                          onPaymentResponseProblem={onPaymentResponseProblem}
                          onPriceChange={handlePriceChange}
                          paymentLinkCartId={paymentLinkCartId}
                          paymentMethods={eligiblePaymentMethods}
                          populatePriceChangeDisplayed={populatePriceChangeDisplayed}
                          refetchCart={refetchCart.current}
                          refetchPaymentQuery={refetchPaymentQuery}
                          selectedMainPaymentMethod={selectedMainPaymentMethod}
                          selectedPaymentMethod={currentPaymentMethod}
                          selectedPaymentMethodCode={selectedPaymentMethodCode}
                          selectedPaymentMethodId={selectedPaymentMethodId}
                          setIsCFCModalOpen={setIsCFCModalOpen}
                          setShouldShowLoadingDots={setShouldShowLoadingDots}
                          submittedWithNoPaymentMethod={submittedWithNoPaymentMethod}
                          subtotalPrice={subtotalPrice}
                          transactionId={transactionId}
                          travelers={travelers}
                          triggerPayment={triggerPayment}
                          tripChanges={tripChanges}
                        />
                        {shouldShowTaxFormSection && (
                          <Card data-testid="paymentPage-taxId-inputField">
                            <TaxFormSection isPanAndDoBDisabled={isPanAndDoBDisabled} />
                          </Card>
                        )}
                        {hasPotentialForAdditionalTax && <AdditionalTax />}
                        {isRyanAirTicketingOrMarketingCarrierPresent && isCFCEnabled && (
                          <CFCInformationCard />
                        )}
                        {isPriceChangeEnabled && Boolean(priceChange) && (
                          <PriceChangeNotification diff={priceChange.priceDiff} />
                        )}
                        {businessReceiptEnabled && <BusinessReceipt />}
                        {shouldShowDiscount && (
                          <Discount
                            discountAmount={discountAmount}
                            isPriceChangeDeclined={isPriceChangeDeclined}
                            refetchCart={refetchCart.current}
                            refetchPaymentQuery={refetchPaymentQuery}
                          />
                        )}
                      </Layout>
                    )}
                  </Stack>
                </div>
              </Profiler>
            </div>
          </InView>
        </Stack>
      </PageBlock>
      {errorMessageId !== makeOrderResponseMessages.CART_FAIL_CART && (
        <PaymentDialog
          destination={getDestination(bounds)}
          dialogState={dialogState}
          errorMessageId={errorMessageId}
          isPayInternal={selectedPaymentMethodType === paymentMethodTypes.PAY_INTERNAL}
          onAcceptPriceChange={handleAcceptPriceChange}
          onCloseError={handleOnErrorDismiss}
          onDeclinePriceChange={handleDeclinePriceChange}
          populatePriceChangeDisplayed={populatePriceChangeDisplayed}
          priceChanges={priceChanges}
          selectedPaymentMethod={selectedMainPaymentMethod}
        />
      )}
      <UrlParamErrorDialog />
      {isDev && (
        <DevBar>
          <Collection>
            <DebugButton onClick={() => fillForm(change)}>Fill form</DebugButton>
            <DebugButton onClick={reset}>Reset Form</DebugButton>
            <DebugButtonPaymentPageMockConfig />
            <DebugGraphQlResponses />
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </div>
  );
};

PaymentPage.propTypes = {
  ...paymentPropTypes,
  customerContactInfo: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.shape({
      countryCallingCode: PropTypes.string,
      numberWithoutCountryCode: PropTypes.string,
    }),
  }),
  hasPotentialForAdditionalTax: PropTypes.bool,
  isPanAndDoBDisabled: PropTypes.bool,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  setHasPendingPayment: PropTypes.func,
};

export default PaymentPage;
